/* eslint-disable no-extra-parens */
/* eslint-disable react/display-name */
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import {
    Map,
    HurricaneLayer,
    PanZoomControl,
    MapZoomPicker,
    Icons
// @ts-expect-error : TODO: We need to figure out Common JS - REACT issue with TS
} from '@weatherbug/react';
import { utilities } from '@weatherbug/common';
import { Map as MapboxMap } from 'mapbox-gl';
import { BotMap } from '../common/botMapView/botMapView';
import { translate } from '../../common/translation/translate';
import { reduxConnect } from '../../common';
import { IStoreState } from '../common/interfaces/types';
import { HurricaneLegendUnitSystem } from './constants';
import { HurricanePageMapLegend } from './hurricanePageLegend';
import { ErrorBoundary } from '../common/errorBoundary/errorBoundary';
import { AnimatedLoadingPlaceholder } from '../common/animatedLoadingPlaceholder/animatedLoadingPlaceholder';
import { HurricaneErrorUI } from './errorUI/hurricaneErrorUI';
import { isDataError } from '../common/utilities/isDataError';
import { HurricaneOutlookMapNotification } from './hurricanePanelSection/hurricaneOutlookSection/hurricaneOutlookMapNotification';

const { CloseIcon } = Icons;
const { unitSystemsEnum, unitSystemType } = utilities.units;

interface HurricaneMapProps {
    latitude: number;
    longitude: number;
    mapboxAccessToken: string;    
    mapBaseUrl: string;
    hurricaneBaseUrl: string;
    tileBaseUrl: string;
    tempUnit: number;
    pressureUnit: number;
    rainUnit: number;
    windUnit: number;
    authId: string;
    secret: string;
    stdParameters: Record<string, unknown>;
    unitSystem: keyof typeof HurricaneLegendUnitSystem;
    hurricaneCategories: string[];
    isBot: boolean;
    isHUrricaneWidgetMap: boolean;
    isNoDataMap: boolean;
    pageId: string;
    isFetching: boolean;
    lastError: Record<string, unknown>;
    widgetIsLazyLoaded?: boolean;
}

const mapStoreStateToProps = (storeState: IStoreState) => {
    const { appState, units, hurricane } = storeState ?? {};

    const { unitSystem } = units ?? {};
    const { user, hurricaneBaseUrl, mapBaseUrl, authId, secret, stdParameters, mapboxAccessToken, pageId } = appState?.appState  || {};

    const { IsBot: isBot, ActiveLocation } = user || {};
    const { Latitude: latitude, Longitude: longitude } = ActiveLocation?.Locality || {};
    const { hurricaneIsFetching, lastError } = hurricane || {};

    return {
        pageId,
        latitude,
        longitude,
        hurricaneBaseUrl,
        mapBaseUrl,
        authId,
        secret,
        stdParameters,
        mapboxAccessToken,
        unitSystem: unitSystemsEnum[unitSystem as keyof typeof unitSystemsEnum],
        isBot,
        isFetching: hurricaneIsFetching,
        lastError
    };
};

const mapDispatchToProps = {};

/**
 * This Component will render Hurricane Map UI on Hurricane widget as well as on the Hurricane Page.
 */
export const HurricaneMapWrapper = reduxConnect(mapStoreStateToProps, mapDispatchToProps)(({
    latitude,
    longitude,
    mapboxAccessToken,
    pageId,
    mapBaseUrl,
    hurricaneBaseUrl,
    isHUrricaneWidgetMap,
    widgetIsLazyLoaded,
    authId,
    secret,
    stdParameters,
    unitSystem,
    isFetching,
    lastError,
    isBot }: HurricaneMapProps) => {

    const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
    const prevIsFetchState = useRef(false);

    const [zoomPickerEnabled, setZoomPickerEnabled] = useState(false);

    useEffect(() => {
        if (prevIsFetchState.current && !isFetching && !isInitialLoadComplete) {
            setIsInitialLoadComplete(true);
        }
        prevIsFetchState.current = isFetching;
    }, [isFetching]);

    const dataLoadError = isDataError(lastError);

    const toggleZoomPicker = () => {
        setZoomPickerEnabled(!zoomPickerEnabled);
    };

    if (!isInitialLoadComplete || !Number.isFinite(latitude) || !Number.isFinite(longitude) || !mapboxAccessToken) {
        return (
            <Container>
                <AnimatedLoadingPlaceholder height='100%' radius={1}/>
            </Container>
        );
    }
    if (dataLoadError && isInitialLoadComplete) {
        return (
            <Container>
                <HurricaneErrorUI isMap={true}/>
            </Container>
        );
    }

    const unitType = unitSystem ? HurricaneLegendUnitSystem[unitSystem] : HurricaneLegendUnitSystem.english; 

    return (
        <Container>
            <ErrorBoundary errorDescription={translate('dataLoadErrorMessage')}>
                { !isHUrricaneWidgetMap ? <HurricaneOutlookMapNotification /> : null }
                {
                    isBot || (isHUrricaneWidgetMap && !widgetIsLazyLoaded)
                        ? <BotMap mapContentText={translate('hurricaneMap')} />
                        : <Map 
                            latitude={latitude}
                            longitude={longitude}
                            initialLatitude={latitude}
                            initialLongitude={longitude}
                            mapboxAccessToken={mapboxAccessToken}
                            showCenterMarker={false}
                            enablePan={!isHUrricaneWidgetMap} // disabled pan on widget
                            enableZoom={!isHUrricaneWidgetMap} // disabled zoom on widget
                            initialZoom={4}
                            extension={(
                                map: InstanceType<typeof MapboxMap>, 
                                latitude: number, 
                                longitude: number, 
                                mapZoomOutPressed: boolean, 
                                mapZoomInPressed: boolean, 
                                mapPanToMarkerPressed: boolean, 
                                currentZoomLevel: number,
                                changeZoom: (zoomLevel: number, changeZoomFn: any) => void,
                            ) => map && <HurricaneLayer
                                map={map}
                                authId={authId}
                                secret={secret}
                                stdApiParameters={stdParameters}
                                latitude={latitude}
                                longitude={longitude}
                                hurricaneBaseUrl={hurricaneBaseUrl}
                                legendBaseUrl={mapBaseUrl}
                                zoom={3}
                                unitType={unitType}
                                renderLegend={(legendColours: string[], legendLabels: string[]) => {
                                    return <HurricanePageMapLegend legendColours={legendColours} legendLabels={legendLabels} isHUrricaneWidgetMap={isHUrricaneWidgetMap }  />;
                                }}>
                                { !isHUrricaneWidgetMap /* Ensures we do not render Map picker and controls on widget UI */
                                    ? <div>
                                        <PanZoomControlContainer>
                                            <PanZoomControl 
                                                zoomInPressed={mapZoomInPressed}
                                                zoomOutPressed={mapZoomOutPressed}
                                                panToMarkerPressed={mapPanToMarkerPressed}
                                                toggleZoomRegionPressed={toggleZoomPicker}
                                                zoomPickerEnabled={zoomPickerEnabled} />
                                        </PanZoomControlContainer>
                                        <ZoomPickerContainer>
                                            {zoomPickerEnabled && <MapZoomPicker
                                                zoomPickerMenuPosition='initial'
                                                zoomPickerMenuId='zoomPickerMenuHurricaneScreen'
                                                zoomPickerMenuHeaderStyle={'padding-top: 11px;'}
                                                headerImage={
                                                    <CloseIcon/>
                                                }
                                                onClickHeaderImage={() => {
                                                    setZoomPickerEnabled(false);
                                                }}
                                                currentZoomLevel={currentZoomLevel}
                                                onItemPressed={(newZoomLevel: number) => {
                                                    changeZoom(newZoomLevel, changeZoom);
                                                    setZoomPickerEnabled(false);
                                                }}
                                            />}
                                        </ZoomPickerContainer>
                                    </div> 
                                    : null }
                            </HurricaneLayer>
                            }/>
                }
            </ErrorBoundary>
        </Container>
    );
});

const Container = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    height: 100%;
    position: relative;

    #mapZoomPickerControlContainer {
        right: 60px;
        bottom: 30px;
    }
    
    #layerAttribution {
        bottom: 1px;
    }
`;

const PanZoomControlContainer = styled.div`
    position: absolute;
    right: 8px;
    bottom: 30px;
`;

const ZoomPickerContainer = styled.div`
    position: absolute;
    right: 63px;
    bottom: 85px;
    z-index: 998;
`;
